import { PageHeader, Tearsheet } from '@carbon/ibm-products';
import { Breadcrumb, BreadcrumbItem, Loading } from '@carbon/react';
import { defineMessages, useIntl } from 'react-intl';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import useGetCurrentEntity from 'hooks/useGetCurrentEntity';

import styles from './SpecificParishBookPersonView.module.scss';
import { ParishBookPersonDto, useGetParishBookPersonsQuery, useUpdateParishBookPersonMutation } from 'api/parishBookEndpoints';
import { ParishBookPersonProvider, useParishBookPersonContext } from '../ParishBookPersonContext';
import ParishBookPersonForm from '../ParishBookPersonForm/ParishBookPersonForm';
import sharedMessages from 'Components/utils/sharedMessages';
import {
    CONTACT_PERSON_DESCRIPTION_MAX_LENGTH,
    CONTACT_PERSON_TEXT_MAX_LENGTH,
    isEmailValid,
    isPhoneNumberValid,
} from 'utils/validationUtils';
import parishBookMessages from '../parishBookMessages';

const SpecificParishBookPersonViewContainer = () => {
    const currentEntityId = useGetCurrentEntityId();
    const { data, isFetching } = useGetParishBookPersonsQuery(
        currentEntityId ?? '',
        { skip: !currentEntityId },
    );
    const { id } = useParams();

    const person = useMemo(() => (data ?? []).find((p) => p.id === id) ?? null, [data, id]);

    if (isFetching) return <Loading />;
    if (!person) return null;

    return (
        <ParishBookPersonProvider initialState={person}>
            <SpecificParishBookPersonView />
        </ParishBookPersonProvider>
    );
};

type SpecificParishBookPersonModalProps = {
    personId?: string | null;
    open: boolean;
    onClose: () => void;
};

export const SpecificParishBookPersonModal = ({
    personId,
    open,
    onClose,
}: SpecificParishBookPersonModalProps) => {
    const currentEntityId = useGetCurrentEntityId();
    const { data, isFetching } = useGetParishBookPersonsQuery(
        currentEntityId ?? '',
        { skip: !currentEntityId || !open },
    );

    const person = useMemo(
        () => (data ?? []).find((p) => p.id === personId) ?? null,
        [data, personId],
    );

    if (!open) {
        return null;
    }

    if (isFetching) {
        return <Loading />;
    }

    if (!person) {
        return null;
    }

    return (
        <ParishBookPersonProvider initialState={person}>
            <SpecificParishBookPersonTearsheet person={person} onClose={onClose} />
        </ParishBookPersonProvider>
    );
};

const useSpecificParishBookPersonForm = () => {
    const intl = useIntl();
    const { values } = useParishBookPersonContext();
    const currentEntityId = useGetCurrentEntityId();
    const currentEntity = useGetCurrentEntity();
    const isReadOnly = Boolean(currentEntity?.parentId);
    const [updatePerson, { isLoading }] = useUpdateParishBookPersonMutation();
    const isSubmitDisabled = !values.categoryId
        || !values.firstName.trim()
        || !values.lastName.trim()
        || values.firstName.trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH
        || values.lastName.trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH
        || (values.availability ?? '').trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH
        || (values.description ?? '').trim().length > CONTACT_PERSON_DESCRIPTION_MAX_LENGTH
        || !isEmailValid(values.email ?? '')
        || !isPhoneNumberValid(values.phoneNumber ?? '');

    const handleSubmit = async () => {
        if (isReadOnly || !currentEntityId) return;
        await updatePerson({
            entityId: currentEntityId,
            id: values.id,
            data: {
                id: values.id,
                categoryId: values.categoryId,
                firstName: values.firstName,
                lastName: values.lastName,
                availability: values.availability || undefined,
                description: values.description || undefined,
                phoneNumber: values.phoneNumber || undefined,
                email: values.email || undefined,
                visibleForGuests: values.visibleForGuests || false,
            },
        }).unwrap();
    };

    const fullName = `${values.firstName} ${values.lastName}`;

    return {
        fullName,
        handleSubmit,
        intl,
        isReadOnly,
        isLoading,
        isSubmitDisabled,
    };
};

const SpecificParishBookPersonTearsheet = ({
    person,
    onClose,
}: {
    person: ParishBookPersonDto;
    onClose: () => void;
}) => {
    const {
        fullName,
        handleSubmit,
        intl,
        isLoading,
        isSubmitDisabled,
        isReadOnly,
    } = useSpecificParishBookPersonForm();

    const handleSave = async () => {
        await handleSubmit();
        onClose();
    };

    return (
        <Tearsheet
            open
            title={fullName}
            description={intl.formatMessage(isReadOnly
                ? parishBookMessages.subgroupReadOnlyDescription
                : messages.description)}
            closeIconDescription={intl.formatMessage(sharedMessages.close)}
            hasCloseIcon
            onClose={onClose}
            selectorPrimaryFocus="#parish-person-firstName"
            actions={isReadOnly ? [{
                label: intl.formatMessage(sharedMessages.close),
                kind: 'primary',
                onClick: onClose,
            }] : [
                {
                    label: intl.formatMessage(sharedMessages.cancel),
                    kind: 'secondary',
                    onClick: onClose,
                },
                {
                    label: intl.formatMessage(sharedMessages.save),
                    kind: 'primary',
                    disabled: isLoading || isSubmitDisabled,
                    loading: isLoading,
                    onClick: handleSave,
                },
            ]}
        >
            <div className={styles.form}>
                <ParishBookPersonForm
                    key={person.id}
                    onSubmit={handleSave}
                    isLoading={isLoading}
                    isSubmitDisabled={isSubmitDisabled}
                    hideActions
                    readOnly={isReadOnly}
                />
            </div>
        </Tearsheet>
    );
};

const SpecificParishBookPersonView = () => {
    const {
        fullName,
        handleSubmit,
        intl,
        isLoading,
        isSubmitDisabled,
        isReadOnly,
    } = useSpecificParishBookPersonForm();

    return (
        <div className={styles.container}>
            <Breadcrumb className={styles.breadcrumb}>
                <BreadcrumbItem href="/parish-book">
                    {intl.formatMessage(sharedMessages.parishTeam)}
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                    {fullName}
                </BreadcrumbItem>
            </Breadcrumb>
            <PageHeader
                className={styles.pageHeader}
                title={{ text: fullName }}
            >
                {intl.formatMessage(isReadOnly
                    ? parishBookMessages.subgroupReadOnlyDescription
                    : messages.description)}
            </PageHeader>
            <ParishBookPersonForm
                onSubmit={handleSubmit}
                isLoading={isLoading}
                isSubmitDisabled={isSubmitDisabled}
                readOnly={isReadOnly}
            />
        </div>
    );
};

const messages = defineMessages({
    description: {
        id: 'parishBook.person.editDescription',
        defaultMessage: 'Tutaj możesz zobaczyć i edytować dane osoby.',
    },
});

export default SpecificParishBookPersonViewContainer;
